import React, { useEffect, useCallback, useReducer } from 'react';
import PropTypes from 'prop-types';
import { withTheme } from 'styled-components';

import {
  Button,
  Select,
  Input,
} from '@pearsontechnology/aero-components';

import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import Modal, { ModalContent, ModalActions } from 'components/Modal';
import { publishFormat, publishType, disciplines } from '../../utils/constants';
import messages from './messages';
import {
  FormField,
  InfoCard,
  Note,
  InfoCardList,
  InfoCardLabel,
} from './styles';

const initialState = {
  productId: '',
  type: '',
  discipline: '',
  previewUrl: '',
  note: false,
  typeFeedback: false,
  noteFeedback: false,
  productIdFeedback: false,
  productIdDuplicateFeedback: false,
  disciplineFeedback: false,
  title: '',
  description: '',
  estimatedTime: '',
  disableProductID: false,
  isDuplicateProductId: false,
  disablePublishButton: false,
};

const actionType = Object.freeze({
  TYPE: 'type',
  NOTE: 'note',
  PRODUCT_ID: 'productId',
  DISCIPLINE: 'discipline',
  TITLE: 'title',
  DESCRIPTION: 'description',
  ESTIMATED_TIME: 'estimatedTime',
  DISCIPLINE_FEEDBACK: 'disciplineFeedback',
  PREVIEW_URL: 'previewUrl',
  TYPE_FEEDBACK: 'typeFeedback',
  NOTE_FEEDBACK: 'noteFeedback',
  PRODUCT_ID_FEEDBACK: 'productIdFeedback',
  PRODUCT_ID_DUPLICATE_FEEDBACK: 'productIdDuplicateFeedback',
  INIT: 'init',
  DISABLE_PRODUCT_ID: 'disableProductID',
  IS_DUPLICATE_PRODUCTID: 'isDuplicateProductId',
  DISABLE_PUBLISH_BUTTON: 'disablePublishButton',
});

const disciplineSetting = Object.freeze({
  'A and P': 'MasteringAandP',
  'Astronomy': 'MasteringAstronomy',
  'Biology': 'MasteringBiology',
  'Chemistry': 'MasteringChemistry',
  'Engineering': 'MasteringEngineering',
  'Environmental Science': 'MasteringEnvironmentalScience',
  'Microbiology': 'MasteringMicrobiology',
  'Physics': 'MasteringPhysics',
});

const getDisciplineOptionName = (disciplineValue) => {
  for (const disciplineKey in disciplineSetting) {
    if (disciplineSetting[disciplineKey] === disciplineValue) {
      return disciplineKey;
    }
  }
};

const reducer = (state, action) => {
  if (action.type === actionType.INIT) {
    return initialState;
  }
  const result = { ...state };
  result[action.type] = action.value;
  return result;
};

const ExportClassOnDemand = ({
  visible,
  configSettings,
  onPublish,
  onDismiss,
  isMasteringProductIdUnique,
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const {
    type,
    note,
    noteFeedback,
    typeFeedback,
    productId,
    productIdFeedback,
    productIdDuplicateFeedback,
    discipline,
    disciplineFeedback,
    title,
    description,
    estimatedTime,
    previewUrl,
    disableProductID,
    isDuplicateProductId,
    disablePublishButton,
  } = state;

  const {
    originalProductId,
    productId: configProductId,
    discipline: configDiscipline,
    previewUrl: configPreviewUrl,
    title: configTitle,
    description: configDescription,
    estimatedTime: configEstimatedTime,
  } = configSettings || {};

  /**
 * @description handles type change action
 */
  const typeChange = (event) => {
    dispatch({ type: actionType.TYPE, value: event.currentTarget.value });
    dispatch({ type: actionType.TYPE_FEEDBACK, value: false });
  };

  /**
  * @description handles publication note change action
  */
  const noteChange = (event) => {
    if (event.currentTarget.value.trim()) {
      dispatch({ type: actionType.NOTE, value: event.currentTarget.value });
      dispatch({ type: actionType.NOTE_FEEDBACK, value: false });
    } else {
      dispatch({ type: actionType.NOTE, value: null });
    }
  };

  /**
  * @description handles publication product ID change action
  */
  const productIDChange = (event) => {
    if (event.currentTarget.value.trim()) {
      dispatch({ type: actionType.PRODUCT_ID, value: event.currentTarget.value });
      dispatch({ type: actionType.PRODUCT_ID_FEEDBACK, value: false });
      dispatch({ type: actionType.PRODUCT_ID_DUPLICATE_FEEDBACK, value: false });
    } else {
      dispatch({ type: actionType.PRODUCT_ID, value: null });
    }
  };

  /**
  * @description handles publication discipline change action
  */
  const disciplineChange = (event) => {
    dispatch({ type: actionType.DISCIPLINE, value: disciplineSetting[event.currentTarget.value] });
    dispatch({ type: actionType.DISCIPLINE_FEEDBACK, value: false });
  };

  /**
  * @description handles publication preview url change action
  */
  const previewUrlChange = (event) => {
    if (event.currentTarget.value.trim()) {
      dispatch({ type: actionType.PREVIEW_URL, value: event.currentTarget.value });
    } else {
      dispatch({ type: actionType.PREVIEW_URL, value: null });
    }
  };

  /**
  * @description handles publication textInputs like title,description,estimatedTime change action
  */
  const textInputChange = (event, actionTypeValue) => {
    if (event.currentTarget.value.trim()) {
      dispatch({ type: actionTypeValue, value: event.currentTarget.value });
    } else {
      dispatch({ type: actionTypeValue, value: null });
    }
  };

  /**
   * @description handles modal dismiss and initialization
   */
  const handleDismiss = useCallback(() => {
    initialize();
    onDismiss();
  }, [initialize, onDismiss]);


  const initialize = useCallback(() => {
    dispatch({ type: actionType.INIT });
  }, []);

  const settings = {
    "productId": productId ? productId : "",
    "discipline": discipline ? discipline : "",
    "previewUrl": previewUrl ? previewUrl : "",
    "title": title ? title : "",
    "description": description ? description : "",
    "estimatedTime": estimatedTime ? estimatedTime : "",
  };

  const handlePublish = async () => {
    if (validateFields()) return;
    let validationResponse = true;
    //if disableProductID === true means republish -> no validation required.
    if (!disableProductID) {
      validationResponse = await isMasteringProductIdUnique({ productId });
    }
    if (validationResponse) {
      /* Added to disable the publish button once clicked to avoid multiple publish call. */
      dispatch({ type: actionType.DISABLE_PUBLISH_BUTTON, value: true });
      await onPublish({
        format: publishFormat.CLASS_ON_DEMAND,
        type,
        note,
        productId,
        settings,
      });
    } else {
      dispatch({ type: actionType.PRODUCT_ID_DUPLICATE_FEEDBACK, value: true });
    }
  };

  const validateFields = () => {
    let missingField = false;
    if (!type) {
      missingField = true;
      dispatch({ type: actionType.TYPE_FEEDBACK, value: true });
    }
    if (!note) {
      missingField = true;
      dispatch({ type: actionType.NOTE_FEEDBACK, value: true });
    }
    if (!discipline) {
      missingField = true;
      dispatch({ type: actionType.DISCIPLINE_FEEDBACK, value: true });
    }
    if (!productId) {
      missingField = true;
      dispatch({ type: actionType.PRODUCT_ID_FEEDBACK, value: true });
    }
    if (isDuplicateProductId) {
      missingField = true;
      dispatch({ type: actionType.PRODUCT_ID_DUPLICATE_FEEDBACK, value: true });
    }
    return missingField;
  };

  useEffect(() => {
    initialize();
    if (originalProductId || configProductId) {
      dispatch({ type: actionType.PRODUCT_ID, value: originalProductId || configProductId });
      dispatch({ type: actionType.PRODUCT_ID_FEEDBACK, value: false });
      dispatch({ type: actionType.DISABLE_PRODUCT_ID, value: true });
    }
    if (configDiscipline) {
      dispatch({ type: actionType.DISCIPLINE, value: configDiscipline });
    }
    if (configPreviewUrl) {
      dispatch({ type: actionType.PREVIEW_URL, value: configPreviewUrl });
    }
    if (configTitle) {
      dispatch({ type: actionType.TITLE, value: configTitle });
    }
    if (configDescription) {
      dispatch({ type: actionType.DESCRIPTION, value: configDescription });
    }
    if (configEstimatedTime) {
      dispatch({ type: actionType.ESTIMATED_TIME, value: configEstimatedTime });
    }
  }, [visible, initialize, originalProductId, configProductId, configDiscipline, configPreviewUrl, configTitle, configDescription, configEstimatedTime]);

  useEffect(() => {
    if (isDuplicateProductId) {
      validateFields();
    }
  }, [isDuplicateProductId]);

  const getProductIdFeedback = (productIdFeedback, productIdDuplicateFeedback) => {
    if (productIdFeedback) {
      return intl.formatMessage(messages.productIdFeedback);
    }
    if (productIdDuplicateFeedback) {
      return intl.formatMessage(messages.productIdDuplicateFeedback);
    }
    return "";
  };

  return (
    <Modal icon="duotonePublications" visible={visible} onDismiss={handleDismiss} title={intl.formatMessage(messages.publishClassOnDemand)}>
      <ModalContent>
        <FormField>
          <Input
            label={intl.formatMessage(messages.productId)}
            placeholder={intl.formatMessage(messages.productIdPlaceholder)}
            onChange={productIDChange}
            feedback={getProductIdFeedback(productIdFeedback, productIdDuplicateFeedback)}
            value={productId}
            disabled={disableProductID}
          />
        </FormField>
        <FormField>
          <Input
            label={intl.formatMessage(messages.title)}
            placeholder={intl.formatMessage(messages.titlePlaceholder)}
            onChange={(event) => textInputChange(event, actionType.TITLE)}
            value={title}
          />
        </FormField>
        <FormField>
          <Input
            label={intl.formatMessage(messages.description)}
            placeholder={intl.formatMessage(messages.descriptionPlaceholder)}
            onChange={(event) => textInputChange(event, actionType.DESCRIPTION)}
            value={description}
          />
        </FormField>
        <FormField>
          <Select
            label={intl.formatMessage(messages.discipline)}
            feedback={disciplineFeedback ? intl.formatMessage(messages.disciplineFeedback) : ''}
            defaultValue={discipline}
            onChange={disciplineChange}
            value={getDisciplineOptionName(discipline)}>
            <option hidden disabled value=""></option>
            {Object.keys(disciplines).map((key) => (<option
              key={disciplines[key]}
              value={intl.formatMessage(messages[disciplines[key]])}
            >
              {intl.formatMessage(messages[disciplines[key]])}
            </option>))}
          </Select>
        </FormField>
        <FormField>
          <Input
            label={intl.formatMessage(messages.estimatedTime)}
            placeholder={intl.formatMessage(messages.estimatedTimePlaceholder)}
            onChange={(event) => textInputChange(event, actionType.ESTIMATED_TIME)}
            value={estimatedTime}
          />
        </FormField>
        <FormField>
          <Input
            label={intl.formatMessage(messages.previewUrl)}
            placeholder={intl.formatMessage(messages.previewUrlPlaceholder)}
            onChange={previewUrlChange}
            value={previewUrl}
          />
        </FormField>
        <FormField>
          <Select
            label={intl.formatMessage(messages.publishType)}
            feedback={typeFeedback ? intl.formatMessage(messages.typeFeedback) : ''}
            defaultValue={type}
            onChange={typeChange}>
            <option hidden disabled value=""></option>
            {Object.keys(publishType).map((key) => (<option
              key={publishType[key]}
              value={intl.formatMessage(messages[publishType[key]])}
            >
              {intl.formatMessage(messages[publishType[key]])}
            </option>))}
          </Select>
        </FormField>
        <FormField>
          <Note
            multiline
            label={intl.formatMessage(messages.note)}
            placeholder={intl.formatMessage(messages.publishComment)}
            onChange={noteChange}
            feedback={noteFeedback ? intl.formatMessage(messages.noteFeedback) : ''}
          />
        </FormField>

        <InfoCard>
          <InfoCardLabel>{intl.formatMessage(messages.publishCardInfo)}</InfoCardLabel>
          <InfoCardList>
            <li>{intl.formatMessage(messages.publishCardInfoUpdate)}</li>
            <li>{intl.formatMessage(messages.publishCardInfoRefresh)}</li>
          </InfoCardList>
        </InfoCard>
      </ModalContent> :
      <ModalActions scrim>
        <Button primary onClick={handleDismiss}>{intl.formatMessage(messages.cancelButton)}</Button>
        <Button primary disabled={disablePublishButton} onClick={handlePublish}>{intl.formatMessage(messages.publishAllCaps)}</Button>
      </ModalActions>
    </Modal >);
};

ExportClassOnDemand.propTypes = {
  onDismiss: PropTypes.func,
  visible: PropTypes.bool,
  configSettings: PropTypes.object,
  onPublish: PropTypes.func,
  isMasteringProductIdUnique: PropTypes.func,
};

export default withTheme(ExportClassOnDemand);
